import React from "react";
import { Modal, Table, Tag, Typography } from "antd";
import type { UserInfo } from "@/contexts/user.types";
import { formatRoleLabel, getRoleSlugsForTrial } from "@/utils/userRoles";
import { ROLE_TAG_STYLE, ACCOUNT_TAG_STYLE } from "./roleTagStyle";

const { Text } = Typography;

type MyRolesModalProps = {
  open: boolean;
  onClose: () => void;
  userInfo?: UserInfo;
  /** Trial currently being viewed, listed first. */
  currentTrialId?: string;
};

type AccessRow = {
  key: string;
  trialId: string;
  trialName: string;
  roleSlugs: string[];
  isCurrent: boolean;
};

const buildRows = (
  userInfo: UserInfo | undefined,
  currentTrialId: string | undefined,
): AccessRow[] => {
  const trials = userInfo?.trials ?? [];

  const rows = trials.map((trial) => ({
    key: trial.id,
    trialId: trial.id,
    trialName: trial.name || trial.id,
    roleSlugs: getRoleSlugsForTrial(userInfo, trial.id),
    isCurrent: trial.id === currentTrialId,
  }));

  // The trial in front of the user answers "what am I here?" first.
  return rows.sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    return a.trialName.localeCompare(b.trialName);
  });
};

/**
 * "What am I, and where?" for the signed-in user.
 *
 * Deliberately limited to what the session actually knows: who you are, your
 * account-level standing, and the roles you hold on each trial. Roles are
 * granted per trial in IAM, so the per-trial table is the honest shape -- a
 * single headline role would be a summary of something that varies.
 */
export default function MyRolesModal({
  open,
  onClose,
  userInfo,
  currentTrialId,
}: MyRolesModalProps) {
  const rows = buildRows(userInfo, currentTrialId);
  const name =
    `${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`.trim();

  const columns = [
    {
      title: "Trial",
      dataIndex: "trialName",
      key: "trialName",
      width: "42%",
      render: (trialName: string, row: AccessRow) => (
        <div className="flex flex-col">
          <Text className="text-sm">{trialName}</Text>
          {row.isCurrent ? (
            <Text className="text-[11px] text-[#00000073]">
              Currently viewing
            </Text>
          ) : null}
        </div>
      ),
    },
    {
      title: "Your role",
      dataIndex: "roleSlugs",
      key: "roleSlugs",
      render: (roleSlugs: string[]) =>
        roleSlugs.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {roleSlugs.map((slug) => (
              <Tag key={slug} style={ROLE_TAG_STYLE}>
                {formatRoleLabel(slug)}
              </Tag>
            ))}
          </div>
        ) : (
          <Text className="text-[#00000073] text-sm">No role granted</Text>
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
      title="My roles"
      data-testid="my-roles-modal"
    >
      <div className="flex flex-col gap-4 pt-2" data-testid="my-roles-body">
        <div>
          <p className="m-0 font-semibold text-[#20353E]">{name}</p>
          {userInfo?.email ? (
            <p className="m-0 text-[#00000073] text-xs">{userInfo.email}</p>
          ) : null}
          {userInfo?.company ? (
            <p className="m-0 text-[#00000073] text-xs">{userInfo.company}</p>
          ) : null}

          {userInfo?.is_superuser || userInfo?.client_admin ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {userInfo?.is_superuser ? (
                <Tag style={ACCOUNT_TAG_STYLE}>Superuser</Tag>
              ) : null}
              {userInfo?.client_admin ? (
                <Tag style={ACCOUNT_TAG_STYLE}>Client Admin</Tag>
              ) : null}
            </div>
          ) : null}
        </div>

        <div>
          <p className="m-0 mb-2 font-semibold text-[#20353E] text-sm">
            Roles by trial
          </p>
          <Table<AccessRow>
            columns={columns}
            dataSource={rows}
            size="small"
            pagination={rows.length > 10 ? { pageSize: 10 } : false}
            locale={{ emptyText: "You have no trial access." }}
          />
          <Text className="text-[#00000073] text-xs">
            Roles are granted per trial, so they can differ between the trials
            you can open. Contact your administrator to change them.
          </Text>
        </div>
      </div>
    </Modal>
  );
}
